import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { fetchBlobWithAuth, fetchWithAuth } from "@/lib/api";
import { HistoryQuery } from "@/lib/orderHistoryQuery";
import { toastHandler } from "@/lib/ToastHandler";

export interface HistoryExportStatus {
  id: string;
  status: "pending" | "generating" | "ready" | "error";
  progress: number;
  expiresAt: string;
  filename?: string;
  total?: number;
  message?: string;
}

export function exportRangeError(query: HistoryQuery): string | null {
  if (!query.startDate || !query.endDate)
    return "Selecciona las fechas desde y hasta para exportar.";
  const days =
    (Date.parse(query.endDate) - Date.parse(query.startDate)) / 86400000 + 1;
  if (!Number.isFinite(days) || days < 1)
    return "El rango de fechas es inválido.";
  if (days > 90) return "Reduce el rango a un máximo de 90 días para exportar.";
  return null;
}

export function useOrderHistoryExport() {
  const { user, getAccessToken, isLoading, userDataLoading } = useAuth();
  const identity = user?.sub || user?.email;
  const storageKey = identity ? `neu-history-export:${identity}` : null;
  const [job, setJob] = useState<HistoryExportStatus | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryVersion, setRetryVersion] = useState(0);
  const authRef = useRef({ getAccessToken, storageKey });
  authRef.current = { getAccessToken, storageKey };
  const requestVersion = useRef(0);
  const submittingRef = useRef(false);

  useEffect(() => {
    requestVersion.current += 1;
    setJob(null);
    setError(null);
    setSubmitting(false);
    setDownloading(false);
    submittingRef.current = false;
    let saved: string | null = null;
    try {
      saved = storageKey ? sessionStorage.getItem(storageKey) : null;
    } catch {
      /* Storage may be disabled. */
    }
    setJobId(saved);
    return () => {
      requestVersion.current += 1;
    };
  }, [storageKey]);

  useEffect(() => {
    if (!jobId || isLoading || userDataLoading) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const token = await authRef.current.getAccessToken();
        const result: HistoryExportStatus = await fetchWithAuth(
          `/order/orders/history/exports/${jobId}`,
          token,
        );
        if (cancelled) return;
        setJob(result);
        setError(null);
        if (result.status === "ready") {
          toastHandler({
            title: "CSV listo",
            description: "La exportación está disponible para descargar.",
            variant: "default",
          });
        } else if (result.status !== "error") {
          timer = setTimeout(poll, 3000);
        }
      } catch (err) {
        if (!cancelled)
          setError(
            err instanceof Error
              ? err.message
              : "No se pudo consultar la exportación.",
          );
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, retryVersion, isLoading, userDataLoading]);

  const start = useCallback(async (query: HistoryQuery) => {
    if (submittingRef.current) return;
    const validation = exportRangeError(query);
    if (validation) {
      setError(validation);
      return;
    }
    const version = requestVersion.current;
    const key = authRef.current.storageKey;
    if (!key) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const token = await authRef.current.getAccessToken();
      const { page, pageSize, ...filters } = query;
      const result: HistoryExportStatus = await fetchWithAuth(
        "/order/orders/history/exports",
        token,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(filters),
        },
      );
      if (version !== requestVersion.current) return;
      try {
        sessionStorage.setItem(key, result.id);
      } catch {
        /* The current page still tracks the job. */
      }
      setJob(result);
      setJobId(result.id);
    } catch (err) {
      if (version === requestVersion.current)
        setError(
          err instanceof Error
            ? err.message
            : "No se pudo solicitar la exportación.",
        );
    } finally {
      if (version === requestVersion.current) {
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
  }, []);

  const download = useCallback(async () => {
    if (!job || job.status !== "ready" || !job.filename || downloading) return;
    const version = requestVersion.current;
    setDownloading(true);
    setError(null);
    try {
      const token = await authRef.current.getAccessToken();
      const blob = await fetchBlobWithAuth(
        `/order/orders/history/exports/${job.id}/download`,
        token,
      );
      if (version !== requestVersion.current) return;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = job.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      if (version === requestVersion.current)
        setError(
          err instanceof Error
            ? err.message
            : "No se pudo descargar el archivo.",
        );
    } finally {
      if (version === requestVersion.current) setDownloading(false);
    }
  }, [job, downloading]);

  const dismiss = useCallback(() => {
    requestVersion.current += 1;
    submittingRef.current = false;
    setSubmitting(false);
    setDownloading(false);
    try {
      if (authRef.current.storageKey)
        sessionStorage.removeItem(authRef.current.storageKey);
    } catch {
      /* Optional persistence. */
    }
    setJob(null);
    setJobId(null);
    setError(null);
  }, []);
  const retryStatus = useCallback(
    () => setRetryVersion((version) => version + 1),
    [],
  );
  return {
    job,
    submitting,
    downloading,
    error,
    start,
    download,
    dismiss,
    retryStatus,
    canRetry: Boolean(jobId),
    busy:
      submitting ||
      Boolean(
        jobId &&
        (!job || job.status === "pending" || job.status === "generating"),
      ),
  };
}
